import { queryOptions, useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/api-context';
import { useBudgetId } from '../budget-store';
import { logAndThrowError } from '../../utils/errors';
/**
 * Fetches a budget by its ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance for making requests.
 * @returns {Promise<Budget>} - A promise resolving to the budget data.
 */
const fetchBudget = async (budgetId, api) => {
    const response = await api.get(`/budget/${budgetId}`);
    return response.data;
};
/**
 * Exposed query options for fetching a budget.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance.
 * @returns Query options for React Query.
 */
export const getBudgetQueryOptions = (budgetId, api) => queryOptions({
    queryKey: ['budget', budgetId],
    queryFn: () => fetchBudget(budgetId, api),
});
/**
 * React Query hook for fetching a budget by its ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {UseBudgetOptions} options - Optional configuration for the query.
 * @returns React Query response object.
 */
export const useGetBudgetApi = (budgetId, { queryConfig } = {}) => {
    const api = useApi();
    return useQuery({
        ...getBudgetQueryOptions(budgetId, api),
        ...queryConfig,
    });
};
/**
 * Hook to get the current budget based on the active budget ID.
 *
 * @returns {Budget} - The currently selected budget.
 * @throws Will throw an error if the budget is not set.
 */
export const useBudget = () => {
    const budgetId = useBudgetId();
    const { data } = useGetBudgetApi(budgetId);
    if (!data) {
        logAndThrowError('Budget not set');
    }
    return data;
};
